/**
 * Invite acceptance (`/invite/:token`) — docs/08-frontend-and-ux.md §3.1,
 * the only account-creation path (ADR-0007).
 *
 * Step 2 of the doc's wireframe — "who are you in the tree?" — needs person
 * search, which arrives with persons in M1; the account half is complete here and
 * the linking is an administrator action (docs/05 §11.3) either way.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PASSWORD_MIN_LENGTH } from '@famtree/shared';
import { authApi } from '../../api/client.js';
import { sessionQueryKey } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import styles from '../../ui/components.module.css';

export function AcceptInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => authApi.invitePreview(token),
    retry: false,
    enabled: token.length > 0,
  });

  const accept = useMutation({
    mutationFn: () => authApi.acceptInvite(token, password, displayName || undefined),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
      navigate('/', { replace: true });
    },
  });

  if (preview.isLoading) {
    return (
      <main className={styles.centeredPage}>
        <p className={styles.muted}>{t('app.loading')}</p>
      </main>
    );
  }

  if (preview.isError) {
    return (
      <main className={styles.centeredPage}>
        <div className={`${styles.centeredPanel} ${styles.card}`}>
          <p className={styles.problem} role="alert">
            {problemMessage(preview.error, t)}
          </p>
          <a href="/login">{t('login.title')}</a>
        </div>
      </main>
    );
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    accept.mutate();
  };

  return (
    <main className={styles.centeredPage}>
      <div className={styles.centeredPanel}>
        <div className={styles.brand}>
          <h1 className={styles.brandName}>{t('invite.title')}</h1>
          {preview.data?.invited_by_display_name && (
            <p className={styles.brandTagline}>
              {t('invite.invitedBy', { name: preview.data.invited_by_display_name })}
            </p>
          )}
        </div>

        <form className={styles.card} onSubmit={onSubmit} noValidate>
          <p className={styles.muted}>
            {t('invite.forEmail', { email: preview.data?.email ?? '' })}
          </p>

          {accept.isError && (
            <p className={styles.problem} role="alert">
              {problemMessage(accept.error, t)}
            </p>
          )}
          {mismatch && (
            <p className={styles.problem} role="alert">
              {t('invite.mismatch')}
            </p>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="display_name">
              {t('invite.displayName')}
            </label>
            <input
              id="display_name"
              className={styles.input}
              value={displayName || (preview.data?.display_name ?? '')}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              {t('invite.password')}
            </label>
            <input
              id="password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirm">
              {t('invite.confirm')}
            </label>
            <input
              id="confirm"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <button className={styles.button} type="submit" disabled={accept.isPending}>
            {accept.isPending ? t('invite.submitting') : t('invite.submit')}
          </button>
        </form>
      </div>
    </main>
  );
}
